import { supabase } from '../lib/supabase';

/**
 * Send email via Resend edge function
 * @param {string} to - Recipient email address
 * @param {string} subject - Email subject
 * @param {string} html - Email HTML content
 * @param {string} type - Email type (signup, notification, etc.)
 * @returns {Promise<{success: boolean, error?: string}>}
 */
const sendEmail = async (to, subject, html, type = 'general') => {
  try {
    const { data, error } = await supabase?.functions?.invoke('send-email', {
      body: {
        to,
        subject,
        html,
        type
      }
    });

    if (error) {
      // Return simplified error for logging
      const errorMessage = error?.message || 'Failed to send email';
      console.error('Error sending email:', errorMessage);
      return { success: false, error: errorMessage };
    }

    // Check if the edge function returned an error in the data
    if (data?.error) {
      return { success: false, error: data?.error };
    }

    return { success: true, data };
  } catch (error) {
    return { success: false, error: error?.message || 'Failed to send email' };
  }
};

/**
 * Send signup validation email
 * @param {string} email - User email address
 * @param {string} fullName - User full name
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const sendSignupValidationEmail = async (email, fullName) => {
  const subject = 'Welcome to PowerPlay Amoeboids - Verify Your Email';
  
  const html = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Verify Your Email</title>
      </head>
      <body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: #f4f4f4;">
        <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f4f4f4; padding: 20px;">
          <tr>
            <td align="center">
              <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
                <!-- Header -->
                <tr>
                  <td style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 40px 20px; text-align: center;">
                    <h1 style="color: #ffffff; margin: 0; font-size: 28px;">PowerPlay Amoeboids</h1>
                  </td>
                </tr>
                
                <!-- Content -->
                <tr>
                  <td style="padding: 40px 30px;">
                    <h2 style="color: #333333; margin: 0 0 20px 0; font-size: 24px;">Welcome, ${fullName}!</h2>
                    <p style="color: #666666; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
                      Thank you for signing up for PowerPlay! We're excited to have you join our community of cricket enthusiasts.
                    </p>
                    <p style="color: #666666; font-size: 16px; line-height: 1.6; margin: 0 0 30px 0;">
                      Your account has been successfully created. You can now start making predictions, joining tournaments, and competing with other users!
                    </p>
                    
                    <div style="background-color: #f8f9fa; border-left: 4px solid #667eea; padding: 15px; margin: 0 0 30px 0;">
                      <p style="color: #333333; font-size: 14px; margin: 0; line-height: 1.5;">
                        <strong>Getting Started:</strong><br>
                        • Browse active tournaments<br>
                        • Make your predictions<br>
                        • Track your performance on the leaderboard<br>
                        • Earn points and climb the rankings
                      </p>
                    </div>
                    
                    <p style="color: #666666; font-size: 14px; line-height: 1.6; margin: 0;">
                      If you have any questions or need assistance, feel free to reach out to our support team.
                    </p>
                  </td>
                </tr>
                
                <!-- Footer -->
                <tr>
                  <td style="background-color: #f8f9fa; padding: 20px 30px; text-align: center; border-top: 1px solid #e9ecef;">
                    <p style="color: #999999; font-size: 12px; margin: 0; line-height: 1.5;">
                      © 2026 PowerPlay. All rights reserved.<br>
                      This is an automated email, please do not reply.
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </body>
    </html>
  `;

  return sendEmail(email, subject, html, 'signup');
};

/**
 * Send notification email
 * @param {string} email - User email address
 * @param {string} title - Notification title
 * @param {string} message - Notification message
 * @param {string} type - Notification type (deadline, result, leaderboard, tournament)
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const sendNotificationEmail = async (email, title, message, type = 'general') => {
  const subject = `PowerPlay - ${title}`;
  
  const typeColors = {
    deadline: '#f59e0b',
    result: '#10b981',
    leaderboard: '#3b82f6',
    tournament: '#8b5cf6',
    general: '#667eea'
  };
  
  const typeColor = typeColors?.[type] || typeColors?.general;
  
  const html = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${title}</title>
      </head>
      <body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: #f4f4f4;">
        <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f4f4f4; padding: 20px;">
          <tr>
            <td align="center">
              <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
                <!-- Header -->
                <tr>
                  <td style="background-color: ${typeColor}; padding: 30px 20px; text-align: center;">
                    <h1 style="color: #ffffff; margin: 0; font-size: 24px;">PowerPlay Amoeboids</h1>
                  </td>
                </tr>
                
                <!-- Content -->
                <tr>
                  <td style="padding: 40px 30px;">
                    <h2 style="color: #333333; margin: 0 0 20px 0; font-size: 22px;">${title}</h2>
                    <p style="color: #666666; font-size: 16px; line-height: 1.6; margin: 0;">
                      ${message}
                    </p>
                  </td>
                </tr>
                
                <!-- Footer -->
                <tr>
                  <td style="background-color: #f8f9fa; padding: 20px 30px; text-align: center; border-top: 1px solid #e9ecef;">
                    <p style="color: #999999; font-size: 12px; margin: 0; line-height: 1.5;">
                      © 2026 PowerPlay. All rights reserved.<br>
                      This is an automated email, please do not reply.
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </body>
    </html>
  `;

  return sendEmail(email, subject, html, type);
};

/**
 * Send bulk notification emails to multiple users
 * @param {Array<{email: string, title: string, message: string}>} notifications - Array of notification objects
 * @param {string} type - Notification type
 * @returns {Promise<{success: number, failed: number, errors: Array}>}
 */
export const sendBulkNotificationEmails = async (notifications, type = 'general') => {
  const results = {
    success: 0,
    failed: 0,
    errors: []
  };

  for (const notification of notifications) {
    try {
      const result = await sendNotificationEmail(
        notification?.email,
        notification?.title,
        notification?.message,
        type
      );

      if (result?.success) {
        results.success++;
      } else {
        results.failed++;
        results?.errors?.push({
          email: notification?.email,
          error: result?.error
        });
      }
    } catch (error) {
      results.failed++;
      results?.errors?.push({
        email: notification?.email,
        error: error?.message
      });
    }
  }

  return results;
};

/**
 * Send deadline reminder email
 */
export const sendDeadlineReminderEmail = async (
  email,
  userName,
  matchDetails,
  minutesRemaining
) => {
  const subject = `⏰ Prediction Deadline Alert - ${matchDetails?.team1} vs ${matchDetails?.team2}`;
  
  const html = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Prediction Deadline Reminder</title>
      </head>
      <body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: #f4f4f4;">
        <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f4f4f4; padding: 20px;">
          <tr>
            <td align="center">
              <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
                <!-- Header -->
                <tr>
                  <td style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 40px 20px; text-align: center;">
                    <h1 style="color: #ffffff; margin: 0; font-size: 28px;">⏰ Prediction Deadline Alert</h1>
                  </td>
                </tr>
                
                <!-- Content -->
                <tr>
                  <td style="padding: 40px 30px;">
                    <h2 style="color: #333333; margin: 0 0 20px 0; font-size: 24px;">Hi ${userName},</h2>
                    <p style="color: #666666; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
                      Time is running out! You have <strong style="color: #667eea;">only ${minutesRemaining} minutes left</strong> to submit your predictions for:
                    </p>
                    
                    <div style="background-color: #f8f9fa; border-left: 4px solid #667eea; padding: 20px; margin: 0 0 30px 0; border-radius: 4px;">
                      <h3 style="color: #333333; margin: 0 0 10px 0; font-size: 18px;">${matchDetails?.team1} vs ${matchDetails?.team2}</h3>
                      <p style="color: #666666; font-size: 14px; margin: 0; line-height: 1.5;">
                        <strong>Tournament:</strong> ${matchDetails?.tournament}<br>
                        <strong>Match:</strong> #${matchDetails?.matchNumber}<br>
                        <strong>Venue:</strong> ${matchDetails?.venue}<br>
                        <strong>Time:</strong> ${matchDetails?.matchTime}
                      </p>
                    </div>
                    
                    <div style="text-align: center; margin: 0 0 30px 0;">
                      <a href="${window?.location?.origin}/match-predictions/${matchDetails?.matchId}" style="display: inline-block; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: #ffffff; text-decoration: none; padding: 15px 40px; border-radius: 6px; font-size: 16px; font-weight: bold;">Make Your Predictions Now</a>
                    </div>
                    
                    <p style="color: #999999; font-size: 14px; line-height: 1.6; margin: 0; text-align: center;">
                      Don't miss out on earning points! Submit your predictions before the deadline.
                    </p>
                  </td>
                </tr>
                
                <!-- Footer -->
                <tr>
                  <td style="background-color: #f8f9fa; padding: 20px 30px; text-align: center; border-top: 1px solid #e9ecef;">
                    <p style="color: #999999; font-size: 12px; margin: 0; line-height: 1.5;">
                      © 2026 PowerPlay. All rights reserved.<br>
                      You received this email because you're participating in ${matchDetails?.tournament}.
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </body>
    </html>
  `;

  return sendEmail(email, subject, html, 'deadline');
};

/**
 * Fetch tournament participants with their email addresses
 * @param {string} tournamentId
 * @returns {Promise<Array<{email: string, fullName: string}>>}
 */
const getTournamentParticipantsWithEmail = async (tournamentId) => {
  try {
    const { data, error } = await supabase
      ?.from('tournament_participants')
      ?.select(`
        user_id,
        user_profiles(email, full_name)
      `)
      ?.eq('tournament_id', tournamentId);

    if (error) {
      console.error('Error fetching participants:', error);
      return [];
    }

    return (data || [])
      ?.filter(p => p?.user_profiles?.email)
      ?.map(p => ({
        email: p?.user_profiles?.email,
        fullName: p?.user_profiles?.full_name || 'Participant'
      }));
  } catch (err) {
    console.error('Error fetching participants:', err);
    return [];
  }
};

/**
 * Send tournament results posted email to all participants
 * @param {string} tournamentId
 * @param {string} tournamentName
 * @returns {Promise<{success: number, failed: number}>}
 */
export const sendTournamentResultEmail = async (tournamentId, tournamentName) => {
  const participants = await getTournamentParticipantsWithEmail(tournamentId);

  if (!participants?.length) {
    return { success: 0, failed: 0 };
  }

  const leaderboardUrl = `https://powerplay.amoeboids.co/tournament-details/${tournamentId}?tab=leaderboard`;
  const subject = `🏆 Tournament Results Published - ${tournamentName}`;

  let successCount = 0;
  let failedCount = 0;

  for (const participant of participants) {
    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Tournament Results Published</title>
        </head>
        <body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: #f4f4f4;">
          <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f4f4f4; padding: 20px;">
            <tr>
              <td align="center">
                <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
                  <tr>
                    <td style="background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); padding: 40px 20px; text-align: center;">
                      <h1 style="color: #ffffff; margin: 0; font-size: 28px;">🏆 PowerPlay Amoeboids</h1>
                      <p style="color: #fef3c7; margin: 8px 0 0 0; font-size: 16px;">Tournament Results Are In!</p>
                    </td>
                  </tr>
                  <tr>
                    <td style="padding: 40px 30px;">
                      <h2 style="color: #333333; margin: 0 0 16px 0; font-size: 22px;">Hi ${participant?.fullName},</h2>
                      <p style="color: #666666; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
                        The final results for <strong>${tournamentName}</strong> have been published! Points have been calculated for all predictions.
                      </p>
                      <p style="color: #666666; font-size: 16px; line-height: 1.6; margin: 0 0 30px 0;">
                        Check the final leaderboard to see how you ranked against other participants.
                      </p>
                      <div style="text-align: center; margin: 30px 0;">
                        <a href="${leaderboardUrl}" style="background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); color: #ffffff; text-decoration: none; padding: 14px 32px; border-radius: 6px; font-size: 16px; font-weight: bold; display: inline-block;">
                          View Final Leaderboard
                        </a>
                      </div>
                      <div style="background-color: #fffbeb; border-left: 4px solid #f59e0b; padding: 15px; margin: 20px 0;">
                        <p style="color: #92400e; font-size: 14px; margin: 0; line-height: 1.5;">
                          <strong>Tournament:</strong> ${tournamentName}<br>
                          <strong>Status:</strong> Results Published ✅
                        </p>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td style="background-color: #f8f9fa; padding: 20px 30px; text-align: center; border-top: 1px solid #e9ecef;">
                      <p style="color: #999999; font-size: 12px; margin: 0; line-height: 1.5;">
                        © 2026 PowerPlay Amoeboids. All rights reserved.<br>
                        This is an automated email, please do not reply.
                      </p>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
          </table>
        </body>
      </html>
    `;

    const result = await sendEmail(participant?.email, subject, html, 'result');
    if (result?.success) {
      successCount++;
    } else {
      failedCount++;
    }
  }

  return { success: successCount, failed: failedCount };
};

/**
 * Send match result posted email to tournament participants
 * @param {string} tournamentId
 * @param {string} tournamentName
 * @param {string} team1Name
 * @param {string} team2Name
 * @param {string} matchNumber
 * @returns {Promise<{success: number, failed: number}>}
 */
export const sendMatchResultEmail = async (tournamentId, tournamentName, team1Name, team2Name, matchNumber) => {
  const participants = await getTournamentParticipantsWithEmail(tournamentId);

  if (!participants?.length) {
    return { success: 0, failed: 0 };
  }

  const leaderboardUrl = `https://powerplay.amoeboids.co/tournament-details/${tournamentId}?tab=leaderboard`;
  const matchLabel = matchNumber ? `Match #${matchNumber}` : 'Match';
  const subject = `📊 Match Result Published - ${team1Name} vs ${team2Name}`;

  let successCount = 0;
  let failedCount = 0;

  for (const participant of participants) {
    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Match Result Published</title>
        </head>
        <body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: #f4f4f4;">
          <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f4f4f4; padding: 20px;">
            <tr>
              <td align="center">
                <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
                  <tr>
                    <td style="background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%); padding: 40px 20px; text-align: center;">
                      <h1 style="color: #ffffff; margin: 0; font-size: 28px;">📊 PowerPlay Amoeboids</h1>
                      <p style="color: #bfdbfe; margin: 8px 0 0 0; font-size: 16px;">Match Result Published</p>
                    </td>
                  </tr>
                  <tr>
                    <td style="padding: 40px 30px;">
                      <h2 style="color: #333333; margin: 0 0 16px 0; font-size: 22px;">Hi ${participant?.fullName},</h2>
                      <p style="color: #666666; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
                        The result for <strong>${matchLabel}: ${team1Name} vs ${team2Name}</strong> in <strong>${tournamentName}</strong> has been published. Points have been calculated for your predictions.
                      </p>
                      <p style="color: #666666; font-size: 16px; line-height: 1.6; margin: 0 0 30px 0;">
                        Check the leaderboard to see your updated ranking.
                      </p>
                      <div style="text-align: center; margin: 30px 0;">
                        <a href="${leaderboardUrl}" style="background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%); color: #ffffff; text-decoration: none; padding: 14px 32px; border-radius: 6px; font-size: 16px; font-weight: bold; display: inline-block;">
                          View Leaderboard
                        </a>
                      </div>
                      <div style="background-color: #eff6ff; border-left: 4px solid #3b82f6; padding: 15px; margin: 20px 0;">
                        <p style="color: #1e40af; font-size: 14px; margin: 0; line-height: 1.5;">
                          <strong>Tournament:</strong> ${tournamentName}<br>
                          <strong>Match:</strong> ${team1Name} vs ${team2Name}<br>
                          <strong>Status:</strong> Result Published ✅
                        </p>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td style="background-color: #f8f9fa; padding: 20px 30px; text-align: center; border-top: 1px solid #e9ecef;">
                      <p style="color: #999999; font-size: 12px; margin: 0; line-height: 1.5;">
                        © 2026 PowerPlay Amoeboids. All rights reserved.<br>
                        This is an automated email, please do not reply.
                      </p>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
          </table>
        </body>
      </html>
    `;

    const result = await sendEmail(participant?.email, subject, html, 'result');
    if (result?.success) {
      successCount++;
    } else {
      failedCount++;
    }
  }

  return { success: successCount, failed: failedCount };
};